import { useEffect } from "react";
import { ArrowRight } from "lucide-react";

// GUIDED PHASE INTRODUCTION — "tell me what I'm doing before I do it." Shown once, automatically, the first time a student reaches a
// major step of the Writing Path; reopened from "Why are we doing this?". Never part of the coaching dialogue, never a trap: one primary
// button, Escape or the backdrop also close it. The cards explain the STRUCTURAL task only — they never supply substantive content.
const CARDS = {
  thesis_first_start: {
    title: "You chose: Start with a thesis",
    body: [
      "First, we'll work toward a thesis — the main organizing claim of your essay.",
      "You do not need to know your thesis yet. Start with anything you are thinking about the assignment:",
      ["a sentence,", "a rough answer,", "a few ideas,", "or a question."],
      "TWS will help you clarify and organize YOUR thinking. TWS will not decide what your thesis should be.",
      "Once you have a working thesis, we'll use it to figure out what the rest of the essay needs to establish.",
    ],
    button: "Start developing my thesis",
  },
  thesis_recognized: {
    title: "You have a working thesis",
    body: (thesis) => [
      "Your thesis — the organizing idea of your essay — is:",
      { quote: thesis },
      "A working thesis does not have to be final. You can revise it as your thinking develops.",
      { label: "Next step", text: "Elaborate the thesis." },
      "Elaborating a thesis means figuring out what the reader will need to understand and what the essay will need to establish for the thesis to make sense and hold up.",
      "We'll use YOUR ideas to identify those parts. TWS will not invent the argument for you.",
      "When the main parts are clear, we'll organize them into the essay.",
    ],
    button: "Set up my argument",
  },
  // ONE introduction for the merged phase DEVELOP THE ESSAY (planning and writing are recursive activities inside it, not rooms).
  develop_essay_start: {
    title: "Now develop the essay",
    body: (thesis) => [
      "You have a working thesis — the main point organizing your essay:",
      { quote: thesis },
      "Now develop the essay around it. As you work, you may:",
      ["figure out what the reader needs to understand,", "write any part,", "discover new ideas,", "move or reorganize parts,", "revise your thesis."],
      "You do not need to finish planning before you write. Writing is one way of figuring out what the essay needs.",
    ],
    button: "Develop my essay",
  },
  develop_essay_explained: {
    title: "Why \u201cDevelop the essay\u201d?",
    body: [
      "You have a working main point. Now develop the essay that makes that point work.",
      "You may work out the argument, write, reorganize, and discover new ideas as you go. Writing itself is part of the thinking.",
      "TWS helps you see what your thesis requires and whether the parts hold together — it will not supply the argument.",
    ],
    button: "Back to my essay",
  },
  build_argument_explained: {
    title: "What does \u201cSet up the argument\u201d mean?",
    body: [
      "Your thesis says what the essay is trying to establish.",
      "The body of the essay has to develop the ideas the reader needs in order to understand and accept that thesis. Your job here is to identify those parts.",
      "A part is not automatically a paragraph. One idea may need several paragraphs, or several related ideas may eventually fit together.",
      "TWS will help you examine and organize the parts you generate, but it will not supply the argument.",
    ],
    button: "Back to my argument",
  },
  organize_start: {
    title: "Now organize the argument",
    body: [
      "You have a thesis and the main parts your essay needs to develop. Now we'll decide how those parts fit together as an essay.",
      "You'll work out:",
      ["what the introduction needs to prepare the reader for,", "how the body parts should be arranged,", "how the parts connect,", "and what the conclusion will eventually need to bring together."],
      "These are jobs for parts of the essay, not fixed paragraph slots. You can change the organization later.",
    ],
    button: "Organize my essay",
  },
  write_start: {
    title: "Now write the essay",
    body: [
      "You have the main structure. Now you'll develop each part in prose.",
      "TWS will keep showing you:",
      ["where you are,", "what this part is supposed to do,", "what still needs developing,", "and what comes next."],
      "You can move around the essay and revise the plan whenever you need to.",
      "TWS will help you develop YOUR ideas without writing the content for you.",
    ],
    button: "Start writing",
  },
  strengthen_start: {
    title: "Now strengthen the essay",
    body: [
      "First we'll look at the essay as a whole. Then we'll look at its major parts. Only after the larger structure works will we focus closely on sentences.",
      { label: "The order", text: "whole essay \u2192 parts \u2192 sentences \u2192 final review" },
      "This helps you avoid polishing sentences that may later need to move or change.",
    ],
    button: "Strengthen my essay",
  },
  ideas_first_start: {
    title: "You chose: Start with ideas",
    body: [
      "You do not need a thesis yet.",
      "Begin with anything that seems important, interesting, puzzling, or relevant to the assignment. Write some ideas in your own words — or write exploratory prose anywhere in the essay. TWS will help you look at how they relate.",
      "Later, we'll work out what you want the essay to say.",
    ],
    button: "Start with my ideas",
  },
  ideas_to_thesis: {
    title: "Now look for the thesis",
    body: [
      "You have several ideas on the page. The next step is to figure out what they add up to.",
      "A thesis is the main claim that organizes the essay. We'll look at the ideas YOU generated and ask what you want the essay to say about them.",
      "TWS may help you notice relationships you already expressed, but it will not decide what those relationships mean.",
    ],
    button: "Work toward my thesis",
  },
  draft_first_start: {
    title: "You chose: Start from existing writing",
    body: [
      "You already have writing to work with.",
      "First, we'll look at what your draft seems to be saying and help you identify the thesis — the main organizing claim of the essay.",
      "TWS will not rewrite the draft or decide what the thesis should be.",
      "We'll use what YOU have already written to clarify the main point and then organize the rest of the essay around it.",
    ],
    button: "Look at my draft",
  },
};

export const INTRO_CARDS = CARDS;

function Line({ item, i }) {
  if (Array.isArray(item)) return <ul className="list-disc pl-6 space-y-0.5 text-[16px] text-stone-800">{item.map((x, j) => <li key={j}>{x}</li>)}</ul>;
  if (item && item.quote !== undefined) return <blockquote data-testid="wp-intro-thesis" className="border-l-2 border-[#8C3A2A] pl-4 font-serif-display text-[19px] leading-snug text-stone-900">{"\u201c"}{item.quote}{"\u201d"}</blockquote>;
  if (item && item.label) return <p className="text-[16px] text-stone-900"><span className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A] mr-2">{item.label}</span>{item.text}</p>;
  return <p key={i} className="text-[16px] leading-relaxed text-stone-800">{item}</p>;
}

export default function WritingPhaseIntroCard({ transition, thesis, onDismiss }) {
  const card = CARDS[transition];
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onDismiss?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);
  if (!card) return null;
  const body = typeof card.body === "function" ? card.body(thesis || "") : card.body;
  return (
    <div data-testid="wp-intro-backdrop" onClick={onDismiss} className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-[2px] flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <section data-testid={`wp-intro-card-${transition}`} role="dialog" aria-labelledby="wp-intro-title" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-[#faf9f6] paper-grain border border-stone-300 rounded-md shadow-2xl p-7 sm:p-9 my-6 wp-intro-enter">
        <p className="font-mono-panel text-[10px] uppercase tracking-[0.24em] text-[#8C3A2A]">Writing Path</p>
        <h2 id="wp-intro-title" data-testid="wp-intro-title" className="font-serif-display text-3xl text-stone-900 mt-2 mb-5 leading-tight">{card.title}</h2>
        <div className="space-y-3.5">{body.map((item, i) => <Line key={i} item={item} i={i} />)}</div>
        <button data-testid="wp-intro-continue" onClick={onDismiss}
          className="mt-7 inline-flex items-center gap-2 bg-[#8C3A2A] text-stone-50 px-6 py-3 rounded-sm text-[15px] hover:bg-[#6B2C20] transition-colors">
          {card.button} <ArrowRight className="h-4 w-4" />
        </button>
      </section>
    </div>
  );
}

// Which introduction belongs to the step the student is on — derived from the Writing Path's existing state, nothing added to it.
export function introTransitionFor({ phase, route, entryStage, needsChoice, organizeStarted }) {
  if (needsChoice) return "";
  if (phase === "develop_main_point") {
    if (route === "ideas_first") return entryStage === "thesis" ? "ideas_to_thesis" : "ideas_first_start";
    if (route === "draft_first") return entryStage === "thesis" ? "" : "draft_first_start";
    return route ? "thesis_first_start" : "";
  }
  if (phase === "build_argument" || phase === "write_essay") return "develop_essay_start";   // one merged phase: no planning → writing transition cards
  if (phase === "strengthen_finish") return "strengthen_start";
  return "";
}

// The explanation "Why are we doing this?" reopens: the step's own card, except in Build the argument, where the thesis card has
// already explained the transition and the link opens what the step itself means.
export function reopenTransitionFor(auto, { route, phase } = {}) {
  if (auto === "thesis_recognized") return "build_argument_explained";
  if (auto === "develop_essay_start") return "develop_essay_explained";
  if (!auto && phase === "develop_main_point" && route === "draft_first") return "draft_first_start";
  return auto;
}
